/**
 * The files of an email draft – the attachments that go out and the
 * review-only files (e.g. a German reading copy) the seat never sends. A file
 * with a local path opens on the owner's Mac: the `jarvis://open` link hands
 * it to Jarvis Helper (`apps/jarvis-mac/…/LocalFileOpener.swift`), which
 * decides what may open. With `compareMeta` (diff mode) a changed attachment
 * list shows the previous one, like the envelope does.
 */
import type { EmailDraftMeta } from "../../api/admin";
import { emailDraftFiles } from "../../lib/emailDrafts";
import { localOpenUrl } from "../../lib/markdownUrls";

export function EmailAttachments({
  meta,
  compareMeta,
}: {
  meta: EmailDraftMeta | null;
  compareMeta?: EmailDraftMeta | null;
}) {
  const files = emailDraftFiles(meta);
  const toSign = files.some((f) => f.needsSignature);
  const sentNames = (m: EmailDraftMeta | null | undefined) =>
    emailDraftFiles(m).filter((f) => !f.reviewOnly).map((f) => f.name).join(", ");
  const names = sentNames(meta);
  const prev = compareMeta !== undefined ? sentNames(compareMeta) : null;
  const changed = prev !== null && prev !== names;
  if (!files.length && !changed) return null;

  return (
    <div className="mb-4 pb-3 border-b border-rule text-xs">
      <div className="text-ink-low mb-1">Attachments · {files.length}</div>
      {toSign ? (
        <p className="text-warn-fg mb-1">
          Sign and save the file. Approval opens when the employee resubmits the signed version.
        </p>
      ) : null}
      <ul className="space-y-1">
        {files.map((f, i) => (
          <li key={`${f.reviewOnly ? "review" : "sent"}-${i}`} className="flex items-center gap-2">
            <span className="font-mono text-ink min-w-0 break-all">{f.name}</span>
            {f.reviewOnly ? <span className="text-ink-low shrink-0">review only – not sent</span> : null}
            {f.needsSignature ? <span className="text-warn-fg shrink-0">needs your signature</span> : null}
            <span className="ml-auto shrink-0">
              {f.localPath ? (
                <a className="btn btn-ghost btn-sm" href={localOpenUrl(f.localPath)} title={f.localPath}>
                  Open
                </a>
              ) : (
                <span className="text-ink-low" title="The employee submitted no local path for this file">
                  no local copy
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {changed ? (
        <div className="text-ink-low line-through mt-1">was: {prev || "—"}</div>
      ) : null}
    </div>
  );
}
